import { useEffect, useState } from 'react'

/**
 * Tracks whether the browser currently has network connectivity.
 *
 * Backed by the `navigator.onLine` flag and the `window` `online`/`offline`
 * events. A `false` value reliably means the browser has no network; a `true`
 * value means a network interface is up (it does not guarantee the internet,
 * or any given backend, is reachable).
 *
 * @returns `true` when the browser reports it is online, `false` otherwise.
 */
export function useOnlineStatus(): boolean {
  // Guard against non-browser environments (SSR/tests) where navigator is absent.
  const [isOnline, setIsOnline] = useState<boolean>(() => (typeof navigator === 'undefined' ? true : navigator.onLine))

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Re-sync in case status changed between initial render and effect.
    setIsOnline(navigator.onLine)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return isOnline
}
